import { RequestAccessResult } from "@app/analytics/MeetingReport/api/types";
import { post, patch, get, httpDelete } from "@app/common/api";
import type {
  EntityAccessLevel,
  SessionACLDeleteRequest,
  SessionACLPatchRequest,
  SessionACLResponse,
  User,
} from "@app/common/types";

export const getSessionACL = async (
  sessionId: string
): Promise<SessionACLResponse> => {
  const json = await get(`/sessions/${sessionId}/acl`, { withCreds: true });
  return json as SessionACLResponse;
};

export const patchSessionACL = async (
  sessionId: string,
  patchRequest: SessionACLPatchRequest
): Promise<void> => {
  // TODO: handle 403's?
  return patch(`/sessions/${sessionId}/acl`, patchRequest, {
    withCreds: true,
  });
};

export const deleteSessionACL = async (
  sessionId: string,
  deleteRequest: SessionACLDeleteRequest
): Promise<void> => {
  const searchParams = new URLSearchParams({
    session_id: deleteRequest.sessionId,
    entity_id: deleteRequest.entityId,
    entity_kind: deleteRequest.kind,
  });

  return httpDelete(`/sessions/${sessionId}/acl?${searchParams.toString()}`, {
    withCreds: true,
  });
};

type RequestAccessPropsTopic = {
  reportType: "topic";
  requesterId: string;
  ownerId: string;
};
type RequestAccessPropsMeeting = {
  reportType: "meeting";
  accessLevel?: Omit<EntityAccessLevel, "owner" | "forbidden" | "no_access">;
};
type RequestAccessProps = { message: string; reportId: string } & (
  | RequestAccessPropsTopic
  | RequestAccessPropsMeeting
);

export const requestAccess = async (
  props: RequestAccessProps
): Promise<RequestAccessResult> => {
  const reportId = props.reportId;
  const message = props.message;
  try {
    if (props.reportType === "topic") {
      await post(
        `/topics/${reportId}/access-requests`,
        {
          topicReportRequesterId: props.requesterId,
          message,
          topicReportOwnerId: props.ownerId,
        },
        { withCreds: true, parseErrorResponse: true }
      );
    } else {
      await post(
        `/sessions/${reportId}/acl/access-requests`,
        { message, accessLevel: props.accessLevel },
        { withCreds: true, parseErrorResponse: true }
      );
    }

    return { status: "pending", success: true };
  } catch (error: any) {
    if ("response" in error) {
      const response: Response = error.response;
      if (error.response.status === 400) {
        const errorBody = await response.json();
        if (errorBody.detail?.includes("already has access")) {
          return { status: "hasAccess", success: true };
        }
      }
    }
    throw error;
  }
};

export const acceptInvite = async (
  reportId: string,
  token: string,
  reportType: "topic" | "meeting"
): Promise<void> => {
  let url;
  if (reportType === "topic") {
    url = `/topics/${reportId}/impersonation/accept-invite`;
  } else {
    url = `/sessions/${reportId}/acl/accept-access-invite`;
  }

  return post(url, { token }, { withCreds: true });
};

export const deleteReport = async (sessionId: string): Promise<void> =>
  httpDelete(`/sessions/${sessionId}`, { withCreds: true });

export const removeReport = async (
  sessionId: string,
  userId: User["id"]
): Promise<void> =>
  deleteSessionACL(sessionId, {
    sessionId,
    entityId: userId,
    kind: "user",
  });
